import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Package, Users, Loader2, Plus, ClipboardList, AlertCircle, X, FileText, HardHat } from 'lucide-react';
import { Card, CardContent } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';
import { ModuleDisabledState } from '@/app/components/module-disabled-state';
import {
  useProjectSafetyData,
  useProjectGearData,
  useProjectCrewData,
} from '@/hooks/use-platform';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { useGearRequests, useCreateGearRequest } from '@/hooks/use-gear-requests';
import type { GearRequest, GearRequestStatus, CreateGearRequestLinePayload } from '@/lib/types';
import { formatDateOnly, formatDateTimeInTimeZone } from '@/lib/date-utils';
import { humanizeEnum } from '@/lib/labels';

// ─── Safety Tab ──────────────────────────────────────────────────────────────

export function ProjectSafetyTab({ projectId, safetyEnabled }: { projectId: number; safetyEnabled: boolean }) {
  const { data, isLoading } = useProjectSafetyData(projectId, safetyEnabled);

  if (!safetyEnabled) {
    return <ModuleDisabledState moduleKey="safety" />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-semibold flex items-center gap-2 mb-3">
          <Shield className="h-4 w-4 text-green-600" />
          JSAs ({data?.jsas?.length ?? 0})
        </h3>
        {data?.jsas && data.jsas.length > 0 ? (
          <div className="space-y-2">
            {data.jsas.map((jsa) => (
              <Link
                key={jsa.id}
                to={`/safety/jsas/${jsa.id}`}
                className="block"
              >
                <Card className="hover:bg-gray-50 transition-colors">
                  <CardContent className="p-3 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">{jsa.title}</p>
                      <p className="text-xs text-gray-500">
                        {jsa.date} {jsa.location && `\u2022 ${jsa.location}`}
                      </p>
                    </div>
                    <Badge variant="outline">{humanizeEnum(jsa.status)}</Badge>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 py-4 text-center">No JSAs linked to this project</p>
        )}
      </div>

      <div>
        <h3 className="font-semibold flex items-center gap-2 mb-3">
          <FileText className="h-4 w-4 text-blue-600" />
          Safe Work Plans ({data?.safe_work_plans?.length ?? 0})
        </h3>
        {data?.safe_work_plans && data.safe_work_plans.length > 0 ? (
          <div className="space-y-2">
            {data.safe_work_plans.map((swp) => (
              <Link key={swp.id} to={`/safety/work-plans/${swp.id}`} className="block">
                <Card className="hover:bg-gray-50 transition-colors">
                  <CardContent className="p-3 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">{swp.title}</p>
                      <p className="text-xs text-gray-500">
                        {swp.date} {swp.location && `\u2022 ${swp.location}`}
                      </p>
                    </div>
                    <Badge variant="outline">{humanizeEnum(swp.status)}</Badge>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 py-4 text-center">No safe work plans linked to this project</p>
        )}
      </div>

      <div>
        <h3 className="font-semibold flex items-center gap-2 mb-3">
          <HardHat className="h-4 w-4 text-orange-600" />
          Rescue Plans ({data?.rescue_plans?.length ?? 0})
        </h3>
        {data?.rescue_plans && data.rescue_plans.length > 0 ? (
          <div className="space-y-2">
            {data.rescue_plans.map((rp) => (
              <Link key={rp.id} to={`/safety/rescue-plans/${rp.id}`} className="block">
                <Card className="hover:bg-gray-50 transition-colors">
                  <CardContent className="p-3 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">{rp.title}</p>
                      <p className="text-xs text-gray-500">
                        {rp.date} {rp.location && `\u2022 ${rp.location}`}
                      </p>
                    </div>
                    <Badge variant="outline">{humanizeEnum(rp.status)}</Badge>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 py-4 text-center">No rescue plans linked to this project</p>
        )}
      </div>

      <div>
        <h3 className="font-semibold flex items-center gap-2 mb-3">
          <ClipboardList className="h-4 w-4 text-indigo-600" />
          Toolbox Talks ({data?.toolbox_talks?.length ?? 0})
        </h3>
        {data?.toolbox_talks && data.toolbox_talks.length > 0 ? (
          <div className="space-y-2">
            {data.toolbox_talks.map((tt) => (
              <Link key={tt.id} to={`/safety/toolbox-talks/${tt.id}`} className="block">
                <Card className="hover:bg-gray-50 transition-colors">
                  <CardContent className="p-3 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">{tt.title}</p>
                      <p className="text-xs text-gray-500">
                        {tt.date} {tt.location && `\u2022 ${tt.location}`}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 py-4 text-center">No toolbox talks linked to this project</p>
        )}
      </div>

      <div>
        <h3 className="font-semibold flex items-center gap-2 mb-3">
          <AlertCircle className="h-4 w-4 text-red-600" />
          Incident Reports ({data?.incident_reports?.length ?? 0})
        </h3>
        {data?.incident_reports && data.incident_reports.length > 0 ? (
          <div className="space-y-2">
            {data.incident_reports.map((ir) => (
              <Link key={ir.id} to={`/safety/incidents/${ir.id}`} className="block">
                <Card className="hover:bg-gray-50 transition-colors">
                  <CardContent className="p-3 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">{ir.title}</p>
                      <p className="text-xs text-gray-500">
                        {ir.date} {ir.location && `\u2022 ${ir.location}`}
                      </p>
                    </div>
                    <Badge variant="outline">{humanizeEnum(ir.status)}</Badge>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 py-4 text-center">No incident reports linked to this project</p>
        )}
      </div>

      <div>
        <h3 className="font-semibold flex items-center gap-2 mb-3">
          <Shield className="h-4 w-4 text-red-500" />
          Near Misses ({data?.near_misses?.length ?? 0})
        </h3>
        {data?.near_misses && data.near_misses.length > 0 ? (
          <div className="space-y-2">
            {data.near_misses.map((nm) => (
              <Card key={nm.id} className="hover:bg-gray-50 transition-colors">
                <CardContent className="p-3 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">{nm.title}</p>
                    <p className="text-xs text-gray-500">
                      {nm.date} {nm.location && `\u2022 ${nm.location}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="capitalize">{nm.severity_potential}</Badge>
                    <Badge variant="outline">{humanizeEnum(nm.status)}</Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 py-4 text-center">No near misses reported for this project</p>
        )}
      </div>

      <div>
        <h3 className="font-semibold flex items-center gap-2 mb-3">
          <Shield className="h-4 w-4 text-purple-600" />
          Rescue Drills ({data?.rescue_drills?.length ?? 0})
        </h3>
        {data?.rescue_drills && data.rescue_drills.length > 0 ? (
          <div className="space-y-2">
            {data.rescue_drills.map((rd) => (
              <Link key={rd.id} to={`/safety/rescue-drills/${rd.id}`} className="block">
                <Card className="hover:bg-gray-50 transition-colors">
                  <CardContent className="p-3 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">Rescue Drill</p>
                      <p className="text-xs text-gray-500">
                        {rd.date} {rd.location && `\u2022 ${rd.location}`}
                      </p>
                    </div>
                    <Badge variant="outline" className="capitalize">{rd.outcome}</Badge>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 py-4 text-center">No rescue drills linked to this project</p>
        )}
      </div>
    </div>
  );
}

// ─── Gear Tab ────────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<GearRequestStatus, string> = {
  pending_confirmation: 'bg-amber-100 text-amber-800',
  requested: 'bg-yellow-100 text-yellow-800',
  fulfilled: 'bg-blue-100 text-blue-800',
  in_transit: 'bg-purple-100 text-purple-800',
  partially_received: 'bg-orange-100 text-orange-800',
  received: 'bg-green-100 text-green-800',
  returned: 'bg-teal-100 text-teal-800',
  cancelled: 'bg-gray-100 text-gray-500',
};

function statusLabel(status: GearRequestStatus): string {
  return humanizeEnum(status);
}

function GearRequestCard({ request }: { request: GearRequest }) {
  const latestEvent = request.events.length > 0
    ? request.events[request.events.length - 1]
    : null;
  const canReceive =
    request.status === 'in_transit' || request.status === 'partially_received';

  return (
    <Card className="hover:bg-gray-50 transition-colors">
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Link
              to={`/gear-requests/${request.id}`}
              className="font-medium text-sm hover:underline"
            >
              Request #{request.id}
            </Link>
            <Badge
              className={`capitalize text-xs ${STATUS_COLORS[request.status]}`}
            >
              {statusLabel(request.status)}
            </Badge>
            {request.priority !== 'normal' && (
              <Badge variant="outline" className="capitalize text-xs">
                {request.priority}
              </Badge>
            )}
          </div>
          <span className="text-xs text-gray-500">
            {request.lines.length} {request.lines.length === 1 ? 'line' : 'lines'}
          </span>
        </div>
        {request.notes && (
          <p className="text-xs text-gray-600 mb-1 truncate">{request.notes}</p>
        )}
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>
            {request.needed_by
              ? `Needed by ${formatDateOnly(request.needed_by)}`
              : 'No deadline'}
            {request.requested_by_username && ` \u2022 ${request.requested_by_username}`}
          </span>
          {latestEvent && (
            <span>
              {statusLabel(latestEvent.to_status as GearRequestStatus)}{' '}
              {formatDateOnly(latestEvent.created_at.slice(0, 10))}
              {latestEvent.actor_username && ` by ${latestEvent.actor_username}`}
            </span>
          )}
        </div>
        {canReceive && (
          <div className="mt-2">
            <Link
              to={`/gear-requests/${request.id}/receive`}
              className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline"
            >
              <Package className="h-3 w-3" />
              Confirm Receipt
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface LineFormRow {
  description: string;
  quantity: number;
  notes: string;
}

const EMPTY_LINE: LineFormRow = { description: '', quantity: 1, notes: '' };

function CreateGearRequestForm({
  projectId,
  onClose,
}: {
  projectId: number;
  onClose: () => void;
}) {
  const [neededBy, setNeededBy] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<LineFormRow[]>([{ ...EMPTY_LINE }]);
  const createMutation = useCreateGearRequest();

  const updateLine = (idx: number, field: keyof LineFormRow, value: string | number) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, [field]: value } : l)));
  };

  const removeLine = (idx: number) => {
    setLines((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));
  };

  const addLine = () => {
    setLines((prev) => [...prev, { ...EMPTY_LINE }]);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validLines: CreateGearRequestLinePayload[] = lines
      .filter((l) => l.description.trim().length > 0)
      .map((l) => ({
        description: l.description.trim(),
        quantity: Math.max(1, l.quantity),
        notes: l.notes.trim(),
      }));

    if (validLines.length === 0) return;

    createMutation.mutate(
      {
        project: projectId,
        needed_by: neededBy || undefined,
        notes: notes.trim() || undefined,
        lines: validLines,
      },
      {
        onSuccess: () => {
          onClose();
        },
      }
    );
  };

  return (
    <Card className="border-blue-200 bg-blue-50/30">
      <CardContent className="p-4">
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="flex items-center justify-between mb-1">
            <h4 className="text-sm font-semibold">New Gear Request</h4>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="gr-needed-by" className="text-xs">
                Needed By
              </Label>
              <Input
                id="gr-needed-by"
                type="date"
                value={neededBy}
                onChange={(e) => setNeededBy(e.target.value)}
                className="h-8 text-sm"
              />
            </div>
            <div>
              <Label htmlFor="gr-notes" className="text-xs">
                Notes
              </Label>
              <Textarea
                id="gr-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional notes..."
                rows={1}
                className="text-sm min-h-[32px] resize-none"
              />
            </div>
          </div>

          <div
            className="space-y-2"
            data-field-container
            data-required="true"
            data-required-empty={
              lines.some((line) => line.description.trim().length > 0) ? 'false' : 'true'
            }
            data-required-message="At least one request line is required."
            tabIndex={-1}
          >
            <Label className="text-xs">Lines</Label>
            {lines.map((line, idx) => (
              <div key={idx} className="flex items-start gap-2">
                <Input
                  placeholder="Description / category"
                  value={line.description}
                  onChange={(e) => updateLine(idx, 'description', e.target.value)}
                  className="h-8 text-sm flex-1"
                />
                <Input
                  type="number"
                  min={1}
                  value={line.quantity}
                  onChange={(e) =>
                    updateLine(idx, 'quantity', parseInt(e.target.value, 10) || 1)
                  }
                  className="h-8 text-sm w-16"
                  title="Quantity"
                />
                <Input
                  placeholder="Line notes"
                  value={line.notes}
                  onChange={(e) => updateLine(idx, 'notes', e.target.value)}
                  className="h-8 text-sm flex-1"
                />
                {lines.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 shrink-0"
                    onClick={() => removeLine(idx)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
            ))}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={addLine}
              className="text-xs h-7"
            >
              <Plus className="h-3 w-3 mr-1" />
              Add line
            </Button>
          </div>

          {createMutation.isError && (
            <p className="text-xs text-red-600 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              Failed to create request. Please try again.
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              className="h-7 text-xs"
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin mr-1" />
              ) : null}
              Create Request
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

export function ProjectGearTab({ projectId, inventoryEnabled }: { projectId: number; inventoryEnabled: boolean }) {
  const orgTimeZone = useOrgTimeZone();
  const { data: gearData, isLoading: gearLoading } = useProjectGearData(projectId, inventoryEnabled);
  const {
    data: requestsData,
    isLoading: requestsLoading,
    isError: requestsError,
  } = useGearRequests({ project: projectId }, inventoryEnabled);
  const [showForm, setShowForm] = useState(false);

  if (!inventoryEnabled) {
    return <ModuleDisabledState moduleKey="inventory" />;
  }

  if (gearLoading || requestsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const deployments = gearData?.deployments ?? [];
  const requests = requestsData?.results ?? [];

  return (
    <div className="space-y-6">
      {/* ── Gear Requirements ──────────────────────────────────── */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold flex items-center gap-2">
            <ClipboardList className="h-4 w-4 text-orange-600" />
            Gear Requirements ({requestsData?.count ?? 0})
          </h3>
          {!showForm && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={() => setShowForm(true)}
            >
              <Plus className="h-3 w-3 mr-1" />
              New Request
            </Button>
          )}
        </div>

        {showForm && (
          <div className="mb-3">
            <CreateGearRequestForm
              projectId={projectId}
              onClose={() => setShowForm(false)}
            />
          </div>
        )}

        {requestsLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          </div>
        ) : requestsError ? (
          <p className="text-sm text-red-600 py-4 text-center flex items-center justify-center gap-1">
            <AlertCircle className="h-4 w-4" />
            Failed to load gear requirements.
          </p>
        ) : requests.length > 0 ? (
          <div className="space-y-2">
            {requests.map((req) => (
              <GearRequestCard key={req.id} request={req} />
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 py-4 text-center">
            No gear requirements for this project
          </p>
        )}
      </div>

      {/* ── Deployed Assets ────────────────────────────────────── */}
      <div>
        <h3 className="font-semibold flex items-center gap-2 mb-3">
          <Package className="h-4 w-4 text-blue-600" />
          Deployed Assets ({deployments.length})
        </h3>
        {deployments.length > 0 ? (
          <div className="space-y-2">
            {deployments.map((d) => (
              <Link
                key={d.id}
                to={`/assets/${d.asset_id}`}
                className="block"
              >
                <Card className="hover:bg-gray-50 transition-colors">
                  <CardContent className="p-3 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">
                        {d.asset_manufacturer} {d.asset_model}
                      </p>
                      <p className="text-xs text-gray-500">
                        SN: {d.asset_serial_number} &middot; {d.asset_type}
                      </p>
                    </div>
                    <div className="text-right">
                      <Badge variant="outline" className="capitalize">
                        {d.action.replace('_', ' ')}
                      </Badge>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatDateTimeInTimeZone(d.timestamp, orgTimeZone, {
                          year: 'numeric',
                          month: 'numeric',
                          day: 'numeric',
                        })}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 py-4 text-center">
            No assets deployed to this project
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Crew Tab ────────────────────────────────────────────────────────────────

export function ProjectCrewTab({ projectId, crewEnabled }: { projectId: number; crewEnabled: boolean }) {
  const { data, isLoading } = useProjectCrewData(projectId, crewEnabled);

  if (!crewEnabled) {
    return <ModuleDisabledState moduleKey="crew" />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const crewAssignments = data?.crew_assignments ?? [];
  const projectAssignments = data?.project_assignments ?? [];

  return (
    <div className="space-y-6">
      {crewAssignments.length > 0 && (
        <div>
          <h3 className="font-semibold flex items-center gap-2 mb-3">
            <Users className="h-4 w-4 text-purple-600" />
            Crew Assignments ({crewAssignments.length})
          </h3>
          <div className="space-y-2">
            {crewAssignments.map((a) => (
              <Link
                key={a.id}
                to={`/crew/techs/${a.tech_id}`}
                className="block"
              >
                <Card className="hover:bg-gray-50 transition-colors">
                  <CardContent className="p-3 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">
                        {a.first_name} {a.last_name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {a.job_title || 'Technician'} &middot; {a.role}
                      </p>
                    </div>
                    <div className="text-right">
                      <Badge variant="outline" className="capitalize">{a.status}</Badge>
                      <p className="text-xs text-gray-500 mt-1">{a.start_date}</p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      )}

      {projectAssignments.length > 0 && (
        <div>
          <h3 className="font-semibold flex items-center gap-2 mb-3">
            <Users className="h-4 w-4 text-blue-600" />
            Project Team ({projectAssignments.length})
          </h3>
          <div className="space-y-2">
            {projectAssignments.map((a) => (
              <Link
                key={a.id}
                to={`/crew/techs/${a.tech_id}`}
                className="block"
              >
                <Card className="hover:bg-gray-50 transition-colors">
                  <CardContent className="p-3 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">
                        {a.first_name} {a.last_name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {a.job_title || 'Technician'} &middot; {a.role}
                      </p>
                    </div>
                    <p className="text-xs text-gray-500">{a.start_date}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      )}

      {crewAssignments.length === 0 && projectAssignments.length === 0 && (
        <p className="text-sm text-gray-500 py-4 text-center">No crew assigned to this project</p>
      )}
    </div>
  );
}
